<script setup lang="ts">
import Dropdown from 'primevue/dropdown';
import { useField } from 'vee-validate';
import {
  computed,
  inject,
  nextTick,
  onMounted,
  reactive,
  shallowRef,
  watch,
} from 'vue';

import currencies from 'lib/assets/json/currencies.json';
import { FieldValidation } from 'lib/components/form/Form.vue.d';
import { getCurrency } from 'lib/components/inputcurrency/helpers/index';
import { useWangsVueConfig } from 'lib/plugins/WangsVue';

import type {
  CurrencyFormat,
  CurrencyValue,
  InputCurrencyEmits,
  InputCurrencyProps,
} from './InputCurrency.vue.d';
import Icon from '../icon/Icon.vue';
import InputNumber from '../inputnumber/InputNumber.vue';
import ValidatorMessage from '../validatormessage/ValidatorMessage.vue';

const props = defineProps<InputCurrencyProps>();
const emit = defineEmits<InputCurrencyEmits>();

onMounted(() => {
  if (props.useValidator) {
    Object.assign(
      field,
      useField(props.fieldName ?? 'currencyInput', (value?: CurrencyValue) => {
        if (!value?.value && props.mandatory) {
          if (
            typeof props.validatorMessage === 'object' &&
            'empty' in props.validatorMessage
          ) {
            return props.validatorMessage.empty.replace('{label}', props.label);
          }

          return localeConfig.emptyInputErrorMessage?.replace(
            '{label}',
            props.label,
          );
        }

        return true;
      }),
    );

    if (props.modelValue) field.value = props.modelValue;
  }
});

const { locale: localeConfig } = useWangsVueConfig('InputCurrency');
const { locale: dropdownLocaleConfig } = useWangsVueConfig('Dropdown');

const { dropdown: DropdownPreset = {}, inputcurrency: Preset = {} } = inject<
  Record<string, any>
>('preset', {});

const isShowOverlay = shallowRef<boolean>(false);
const numberInput = shallowRef<number | undefined>(props.modelValue?.value);
const isChromeBased = navigator.userAgent.includes('Chrome');
const selectedCurrency = shallowRef<CurrencyFormat>(
  getCurrency(props.modelValue?.currency),
);

const field = reactive<FieldValidation<CurrencyValue | undefined>>({
  value: props.modelValue,
});

const currencyOptions = shallowRef<CurrencyFormat[]>(currencies);

const locale = computed(() => selectedCurrency.value?.locale);
const inputNumberId = computed(() => {
  if (props.inputnNumberId) return props.inputnNumberId;

  return (
    (props.label ?? props.fieldName ?? 'input-number')
      .toLowerCase()
      .trim()
      .replaceAll(' ', '-') + '-inputnumber'
  );
});

const invalidInput = computed(() => props.invalid || !!field.errorMessage);

const updateFieldValue = (
  { currency }: CurrencyFormat,
  value?: number,
  updateCurrency: boolean = false,
): void => {
  const fieldValue = { currency, value };
  emit('update:modelValue', fieldValue);
  if (props.useValidator) {
    field.setValue(fieldValue, !updateCurrency); // Don't trigger validation when changing currency
  } else {
    field.value = fieldValue;
  }
};

/**
 * Focusing input number after user select dial code
 */
const focusInput = (el: HTMLDivElement): void => {
  nextTick(() => {
    const input = el.querySelector(
      `#${inputNumberId.value}`,
    ) as HTMLInputElement | null;

    if (input) input.focus();
  });
};

const updateSelectedCurrency = (val?: CurrencyValue): void => {
  if (val?.currency != selectedCurrency.value?.currency) {
    // Only perform assignment only when necessary, when the currency code is the same, it should not re-assign.
    selectedCurrency.value = val?.currency
      ? getCurrency(val.currency)
      : selectedCurrency.value;
  }
};

watch(
  () => props.modelValue,
  (val) => {
    updateSelectedCurrency(val);
    field.value = val;
    numberInput.value = val?.value;
  },
);

watch(
  () => field.value,
  (val) => {
    updateSelectedCurrency(val);
    numberInput.value = val?.value;
  },
);
</script>

<template>
  <InputNumber
    v-bind="props"
    v-model="numberInput"
    :input-id="inputNumberId"
    :input-number-class="Preset.value({ isChromeBased, invalidInput }).class"
    :locale="locale"
    :min-fraction-digits="0"
    :use-validator="false"
    @update:model-value="updateFieldValue(selectedCurrency, $event)"
    addon-left-class="!p-0 !bg-transparent"
    input-group-class="!ring-0"
    mode="decimal"
    use-grouping
  >
    <template #addon-left>
      <Dropdown
        v-model="selectedCurrency"
        :filter-fields="['label', 'currency']"
        :filter-placeholder="dropdownLocaleConfig.filterPlaceholder"
        :options="currencyOptions"
        :pt="{
          root: {
            class: [
              DropdownPreset.root({
                props: { modelValue: selectedCurrency },
                state: {},
              }).class,
              Preset.currency({ isChromeBased, invalidInput }).class,
            ],
          },
          input: {
            class: [
              'py-1 pl-3 pr-0.5',
              DropdownPreset.input({ props: { modelValue: selectedCurrency } })
                .class,
            ],
          },
        }"
        @hide="(isShowOverlay = false), focusInput($el)"
        @show="isShowOverlay = true"
        @update:model-value="updateFieldValue($event, numberInput, true)"
        data-key="currency"
        filter
        option-label="symbol"
      >
        <template #dropdownicon>
          <Icon
            v-bind="DropdownPreset.dropdownicon({ state: { isShowOverlay } })"
          />
        </template>

        <template #option="{ option }">
          {{ option.label }}
        </template>
      </Dropdown>
    </template>

    <template #validatormessage>
      <ValidatorMessage :message="field.errorMessage" />
    </template>
  </InputNumber>
</template>
